import { z } from 'zod';

import { apiSuccess, apiList } from '@services/apiSchemas';

import { operatorApiFetch } from './apiService';

// ============================================================
// Schemas — 對齊 app/controllers/api/operator/v1/brands_controller.rb
// ============================================================

// `index` 端點（fetchList）— `select(:id, :name, :merchant_id)` + as_json，shape 最輕
const BrandListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  merchant_id: z.string(),
}).passthrough();

// `get_records` 端點 — block 回 5 個欄位
const BrandTableRowSchema = z.object({
  id: z.string(),
  merchant_id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  created_at: z.string(),
}).passthrough();

// `show` / `create` / `update` 端點 — 共用 shape（含 editable / deletable / updated_at）
const BrandDetailSchema = z.object({
  id: z.string(),
  merchant_id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  editable: z.boolean(),
  deletable: z.boolean(),
}).passthrough();

// `destroy` 端點 — 回 `{}` + success message
const EmptyObjectSchema = z.object({}).passthrough();

// ============================================================
// Exported types
// ============================================================

export type BrandListItem = z.infer<typeof BrandListItemSchema>;
export type BrandTableRow = z.infer<typeof BrandTableRowSchema>;
export type BrandDetail = z.infer<typeof BrandDetailSchema>;

// ============================================================
// Service
// ============================================================

interface FetchListParams {
  since_id?: string;
  page_size?: number;
  search?: string;
}

const brandService = {
  // 獲取品牌列表（用於表格）
  getRecords: async (params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('brands/get_records', {
      method: 'POST',
      body: params,
    });
    return apiList(BrandTableRowSchema).parse(await res.json());
  },

  // 獲取所有品牌 ID（用於篩選）
  getIds: async (params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('brands/get_ids', {
      method: 'POST',
      body: params,
    });
    return apiSuccess(z.array(z.string())).parse(await res.json());
  },

  // 獲取品牌列表（用於下拉選單）
  fetchList: async (params: FetchListParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.since_id) queryParams.append('since_id', params.since_id);
    if (params.page_size) queryParams.append('page_size', String(params.page_size));
    if (params.search) queryParams.append('search', params.search);

    const res = await operatorApiFetch(`brands?${queryParams.toString()}`, {
      method: 'GET',
    });
    return apiList(BrandListItemSchema).parse(await res.json());
  },

  // 獲取單個品牌
  get: async (id: string) => {
    const res = await operatorApiFetch(`brands/${id}`, {
      method: 'GET',
    });
    return apiSuccess(BrandDetailSchema).parse(await res.json());
  },

  // 創建品牌
  create: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('brands', {
      method: 'POST',
      body: { brand: data },
    });
    return apiSuccess(BrandDetailSchema).parse(await res.json());
  },

  // 更新品牌
  update: async (id: string, data: Record<string, unknown>) => {
    const res = await operatorApiFetch(`brands/${id}`, {
      method: 'PUT',
      body: { brand: data },
    });
    return apiSuccess(BrandDetailSchema).parse(await res.json());
  },

  // 刪除品牌
  delete: async (id: string) => {
    const res = await operatorApiFetch(`brands/${id}`, {
      method: 'DELETE',
    });
    return apiSuccess(EmptyObjectSchema).parse(await res.json());
  },
};

export default brandService;
